/**
 * Custom Role Assignment Component
 * 
 * Allows admins to assign/remove custom roles to/from users
 * 
 * @module CustomRoleAssignment
 */

import React, { useState, useEffect } from 'react';
import { customRoleService, CustomRole } from '@/services/customRoleService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';

interface CustomRoleAssignmentProps {
  userId: string;
  organizationId: string;
  onUpdate?: () => void;
}

const CustomRoleAssignment: React.FC<CustomRoleAssignmentProps> = ({
  userId,
  organizationId,
  onUpdate,
}) => {
  const [availableRoles, setAvailableRoles] = useState<CustomRole[]>([]);
  const [assignedRoleIds, setAssignedRoleIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [organizationId, userId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load available roles
      const rolesResult = await customRoleService.getCustomRolesByOrganization(organizationId);
      if (rolesResult.success && rolesResult.roles) {
        setAvailableRoles(rolesResult.roles.filter(r => r.isActive));
      } else {
        setError(rolesResult.error || 'Failed to load roles');
      }

      // Load user's assigned roles
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const customRoleIds = userData?.organizationRoles?.[organizationId]?.customRoleIds || [];
        setAssignedRoleIds(customRoleIds);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (roleId: string) => {
    setError(null);
    const result = await customRoleService.assignCustomRoleToUser(
      userId,
      roleId,
      organizationId
    );

    if (result.success) {
      setAssignedRoleIds([...assignedRoleIds, roleId]);
      onUpdate?.();
    } else {
      setError(result.error || 'Failed to assign role');
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    if (!confirm('Remove this role from the user?')) {
      return;
    }

    setError(null);
    const result = await customRoleService.removeCustomRoleFromUser(
      userId,
      roleId,
      organizationId
    );

    if (result.success) {
      setAssignedRoleIds(assignedRoleIds.filter(id => id !== roleId));
      onUpdate?.();
    } else {
      setError(result.error || 'Failed to remove role');
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading roles...</p>
      </div>
    );
  }

  if (error && availableRoles.length === 0) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded text-red-800">
        {error}
      </div>
    );
  }

  if (availableRoles.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded">
        <p className="text-gray-600 text-sm">
          No custom roles available. Create custom roles in Settings → Custom Roles.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-lg bg-white">
      <h3 className="text-lg font-semibold mb-4">Custom Roles</h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {availableRoles.map((role) => {
          const isAssigned = assignedRoleIds.includes(role.id);
          return (
            <div
              key={role.id}
              className={`flex items-center justify-between p-3 border rounded transition-colors ${
                isAssigned ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex-1">
                <div className="font-medium text-gray-900">{role.name}</div>
                <div className="text-sm text-gray-600 mt-1">{role.description}</div>
                {isAssigned && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Assigned
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() =>
                  isAssigned
                    ? handleRemoveRole(role.id)
                    : handleAssignRole(role.id)
                }
                className={`ml-4 px-4 py-2 rounded text-sm font-medium transition-colors ${
                  isAssigned
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {isAssigned ? 'Remove' : 'Assign'}
              </button>
            </div>
          );
        })}
      </div>

      {assignedRoleIds.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-600">
            {assignedRoleIds.length} custom role{assignedRoleIds.length !== 1 ? 's' : ''} assigned
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomRoleAssignment;

